<template>
  <div class="flex flex-1 min-h-0 flex-col gap-3 overflow-hidden bg-slate-900/20 p-3">
    <section class="flex-none space-y-2 px-1 py-1">
      <div class="flex flex-wrap items-center justify-between gap-3 px-2">
        <div class="flex items-center gap-5">
          <span class="text-sm font-semibold uppercase tracking-wide text-slate-300">Composer</span>
          <div class="flex items-center overflow-hidden rounded border border-slate-600" role="group" aria-label="Composer mode">
            <button
              v-for="mode in composerModeOptions"
              :key="mode.value"
              type="button"
              class="px-3 py-1 text-xs font-medium transition-colors border-r border-slate-600 last:border-r-0"
              :class="composerMode === mode.value
                ? 'bg-cyan-500/25 text-cyan-100'
                : 'bg-slate-800/60 text-slate-400 hover:bg-slate-700 hover:text-white'"
              :aria-pressed="composerMode === mode.value"
              :title="`${mode.label} (${mode.shortcut})`"
              @click="setComposerMode(mode.value)"
            >{{ composerMode === mode.value ? mode.label.toUpperCase() : mode.label }}</button>
          </div>
          <span v-if="composerMode === 'compose'" class="text-xs text-slate-500">Cursor at tick <strong class="text-amber-300">{{ insertTick }}</strong> · keys Z…M / Q…U · [ ] octave · Alt+1…6 duration · ←→ notes (⌥ plays) · Space rest · Backspace delete</span>
          <span v-else class="text-xs text-slate-500">Practice mode · composer input is paused</span>
        </div>
        <div class="flex items-center gap-3 text-xs text-slate-300">
          <span>Pitch <strong class="text-cyan-200">{{ noteName(selectedPitch) }}</strong></span>
          <span><strong class="text-cyan-200">{{ synth.tempo.grid }}</strong> ({{ defaultGridDurationTicks() }})</span>
          <button type="button" class="rounded border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 font-medium text-cyan-200 transition-colors hover:bg-cyan-500/20 disabled:opacity-40" :disabled="!canInsert" @click="appendNote">+ Note</button>
        </div>
      </div>

      <div class="flex flex-wrap items-stretch gap-2">
        <button
          type="button"
          class="flex-none rounded border px-3 text-lg leading-none transition-colors"
          :class="composerMode === 'compose'
            ? 'border-rose-500/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20'
            : 'border-slate-600 bg-slate-800/50 text-slate-600 cursor-not-allowed'"
          :disabled="composerMode !== 'compose'"
          title="Delete selected/previous note (Backspace)"
          aria-label="Delete selected/previous note (Backspace)"
          @click="deleteComposerEvent"
        >⌫</button>
        <div class="flex flex-none items-center overflow-hidden rounded border border-slate-600 bg-slate-800/50 text-xs text-slate-300">
          <button
            type="button"
            class="h-full px-2 text-base leading-none text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
            title="Octave down ([)"
            aria-label="Octave down"
            @click="shiftKeyboardOctave(-1)"
          >−</button>
          <span class="px-2 font-semibold tabular-nums text-cyan-200" title="PC keyboard base note">{{ keyboardBaseNoteName }}</span>
          <button
            type="button"
            class="h-full px-2 text-base leading-none text-slate-300 transition-colors hover:bg-slate-700 hover:text-white"
            title="Octave up (])"
            aria-label="Octave up"
            @click="shiftKeyboardOctave(1)"
          >+</button>
        </div>
        <div class="flex flex-none items-center gap-1">
          <button
            type="button"
            class="rounded border px-2 py-1 text-xs font-medium transition-colors"
            :class="compositionAssist.enabled
              ? 'border-cyan-500/40 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/25'
              : 'border-slate-600 bg-slate-800/50 text-slate-400 hover:bg-slate-700 hover:text-white'"
            :aria-pressed="compositionAssist.enabled"
            title="Highlight notes in the selected key"
            @click="toggleCompositionAssist"
          >Assist</button>
          <select
            class="rounded border border-slate-600 bg-slate-800/80 px-2 py-1 text-xs text-white outline-none focus:border-cyan-500"
            :value="compositionAssistValue"
            title="Composition key and scale"
            aria-label="Composition key and scale"
            @change="selectCompositionAssist($event.target.value)"
          >
            <option v-for="option in compositionAssistOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </div>
        <div class="min-w-[360px] flex-1 overflow-x-auto rounded border border-slate-600">
          <SynthPitchBar
            :assist="compositionAssist"
            :trackColor="activeTrackColor"
            :minMidi="pitchRange.minMidi"
            :maxMidi="pitchRange.maxMidi"
            :selectedPitch="selectedPitch"
            :activeKeyColors="activeKeyColors"
            :playbackTimeMs="playbackTimeMs"
            :playing="playing"
            @select-pitch="selectPitch"
            @audition-pitch="auditionPitch"
          />
        </div>
      </div>
    </section>

    <div class="flex flex-1 min-h-0 gap-3">
      <aside class="min-h-0 w-[260px] shrink-0 overflow-y-auto rounded-lg border border-slate-600 bg-slate-800/55">
          <div class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-600/70 px-4 py-3">
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span class="text-sm font-semibold uppercase tracking-wide text-slate-300">Track {{ trackIndex + 1 }}</span>
              </div>
              <div class="mt-1 text-xs text-slate-500">{{ statsLabel }}</div>
            </div>
          </div>
          <div class="border-b border-slate-600/70 px-4 py-3">
            <div class="grid gap-3">
              <label class="space-y-1 text-xs uppercase tracking-wide text-slate-500">
                Track name
                <form-input class="w-full text-sm font-semibold normal-case" :modelValue="track.name" aria-label="Track name" @input="renameTrack($event.target.value)" />
              </label>
              <label class="space-y-1 text-xs uppercase tracking-wide text-slate-500">
                Instrument <span v-if="soundModified" class="normal-case text-amber-300">· Modified</span>
                <form-select class="w-full text-sm normal-case text-slate-100" :modelValue="track.instrument" @change="changeInstrument($event.target.value)">
                  <option v-for="option in instrumentOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                </form-select>
              </label>
              <label class="space-y-1 text-xs uppercase tracking-wide text-slate-500">
                Volume {{ Math.round(track.volume * 100) }}%
                <input class="block h-9 w-full accent-cyan-400" type="range" min="0" max="1" step="0.01" :value="track.volume" @input="updateTrackVolume($event.target.value)" />
              </label>
            </div>
            <section v-if="selectedEvent && composerMode === 'compose'" class="mt-3 space-y-3 border-t border-slate-600/70 pt-3">
              <div class="text-xs font-semibold uppercase tracking-wide text-slate-300">Selected Event</div>
              <div class="grid gap-3 text-sm">
                <div class="flex items-baseline gap-4">
                  <div>
                    <div class="text-xs uppercase tracking-wide text-slate-500">Pitch</div>
                    <div class="font-semibold text-white">{{ noteName(selectedEvent.note) }}</div>
                  </div>
                  <div>
                    <div class="text-xs uppercase tracking-wide text-slate-500">Start</div>
                    <div class="font-mono text-white">{{ selectedEvent.startTick }}</div>
                  </div>
                </div>
                <label class="text-xs text-slate-400">
                  Duration
                  <form-select class="mt-1 w-full text-sm" :modelValue="selectedDurationDivision" @change="updateSelectedDurationDivision($event.target.value)">
                    <option v-for="division in durationDivisions" :key="division" :value="division">{{ division }}</option>
                  </form-select>
                </label>
                <div class="text-xs text-slate-500">{{ selectedDurationLabel }}</div>
                <div class="flex flex-wrap gap-2">
                  <button type="button" class="rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs font-medium text-rose-200 hover:bg-rose-500/20" @click="deleteComposerEvent">Delete</button>
                </div>
              </div>
            </section>
            <SynthSoundEditor compact :track="track" @update-sound="updateSound" @reset="resetSound" />
          </div>
      </aside>

      <section class="flex flex-1 min-w-0 min-h-0 flex-col overflow-hidden rounded-lg border border-slate-600 bg-slate-800/30">
        <div class="flex-none border-b border-slate-600/70 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-300">Sequence</div>
        <div class="flex-none px-3 pt-3">
          <SynthSequenceOverview
            :tracks="synth.tracks"
            :bpm="synth.tempo.bpm"
            :playbackTimeMs="playbackTimeMs"
            :viewport="sequenceViewport"
            @seek="$emit('seek', $event)"
            @focus="timelineRef?.focusTime($event)"
            @pan="timelineRef?.setHorizontalScroll($event)"
          />
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto p-3">
          <SynthSequenceTimeline
            ref="timelineRef"
            :tracks="synth.tracks"
            :activeTrackIndex="trackIndex"
            :trackPlaybackEnabledIndices="trackPlaybackEnabledIndices"
            :playbackTimeMs="playbackTimeMs"
            :playing="playing"
            :bpm="synth.tempo.bpm"
            :selectedEventIndex="selectedEventIndex"
            :composeMode="composerMode === 'compose'"
            :cursorTick="insertTick"
            editable
            fillViewport
            showTrackLabels
            showTrackNames
            @select-track="$emit('select-track', $event)"
            @toggle-track-playback="$emit('toggle-track-playback', $event)"
            @update-track="$emit('update-track', $event)"
            @select-event="selectEvent"
            @audition-event="auditionEvent"
            @cursor-tick="placeComposerCursor"
            @viewport-change="sequenceViewport = $event"
          />

        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount, onMounted } from 'vue'
import { useSynthStore } from '../../stores/synthStore.js'
import { midiToNoteName, SYNTH_DEFAULT_NOTE } from '../../stores/synthModel.js'
import { BrowserSynthEngine } from '../../audio/synth/BrowserSynthEngine.js'
import { noteDivisionToTicks, ticksToMs, closestDivisionLabel } from './synthTimebase.js'
import { ticksToNoteValue } from '../../stores/synthTiming.js'
import SynthPitchBar from './SynthPitchBar.vue'
import SynthSequenceTimeline from './SynthSequenceTimeline.vue'
import SynthSequenceOverview from './SynthSequenceOverview.vue'
import SynthSoundEditor from './SynthSoundEditor.vue'
import { buildAbsoluteSequenceTiming } from './synthSequenceGeometry.js'
import { buildActiveKeyColorsFromPlayback } from './synthPlaybackHighlight.js'
import { createTrackComposerSelection } from './synthComposerState.js'
import {
  clampKeyboardBaseMidi,
  composerModeForShortcut,
  COMPOSER_KEYBOARD_BASE_MIDI,
  createComposerInsertCursor,
  createComposerModeState,
  findAdjacentNoteStart,
  findNoteEventsAtStart,
  findPreviousInsertEventIndex,
  insertDurationForShortcut,
  isTextInputTarget,
  keyboardMidi,
} from './synthComposerInsert.js'
import { getSynthTrackColor } from './synthTrackColors.js'
import {
  compositionAssistOptions as buildCompositionAssistOptions,
  compositionAssistValue as buildCompositionAssistValue,
  DEFAULT_COMPOSITION_ASSIST,
  parseCompositionAssistValue,
} from './synthCompositionAssist.js'
import { applyInstrumentPreset, isTrackSoundModified, SYNTH_INSTRUMENT_OPTIONS } from '../../stores/synthInstruments.js'

const props = defineProps({
  synth: { type: Object, required: true },
  trackIndex: { type: Number, required: true },
  trackPlaybackEnabledIndices: { type: Array, default: () => [] },
  composerView: { type: String, default: 'pianoroll' },
  playbackTimeMs: { type: Number, default: 0 },
  playing: { type: Boolean, default: false },
})

const emit = defineEmits(['rename-track', 'update-track', 'update:composerView', 'seek', 'select-track', 'toggle-track-playback', 'update-grid'])
const store = useSynthStore()
const instrumentOptions = SYNTH_INSTRUMENT_OPTIONS
const engine = new BrowserSynthEngine()
const timelineRef = ref(null)
const sequenceViewport = ref({ scrollLeft: 0, viewportWidth: 0, contentWidth: 0 })
const synth = computed(() => props.synth)
const { selectedEventIndex, selectedPitch, selectPitch: setSelectedPitch, selectEvent: setSelectedEvent, resetEventSelection } = createTrackComposerSelection()
const track = computed(() => props.synth.tracks[props.trackIndex])
const soundModified = computed(() => isTrackSoundModified(track.value))
const selectedEvent = computed(() => track.value.sequence[selectedEventIndex.value] ?? null)
const activeTrackTiming = computed(() => buildAbsoluteSequenceTiming(track.value.sequence, synth.value.tempo.bpm))
const { composerMode, setComposerMode } = createComposerModeState()
const { insertTick, setInsertTick, advanceInsertTick, resetInsertTick } = createComposerInsertCursor()
const keyboardBaseMidi = ref(COMPOSER_KEYBOARD_BASE_MIDI)
const keyboardBaseNoteName = computed(() => midiToNoteName(keyboardBaseMidi.value))
const heldKeyboardNotes = ref(new Set())
// Editor-only assist state: it is never written to the synth resource or the .synth file.
const compositionAssist = ref({ ...DEFAULT_COMPOSITION_ASSIST })
const compositionAssistOptions = buildCompositionAssistOptions()
const compositionAssistValue = computed(() => buildCompositionAssistValue(compositionAssist.value.root, compositionAssist.value.scale))
// Same canonical track colour the timeline lanes and held-key highlights use.
const activeTrackColor = computed(() => getSynthTrackColor(props.trackIndex).solid)
const composerModeOptions = Object.freeze([
  { value: 'compose', label: 'Compose', shortcut: 'F1' },
  { value: 'practice', label: 'Practice', shortcut: 'F2' },
])
const activeKeyColors = computed(() => {
  const states = props.playing
    ? buildActiveKeyColorsFromPlayback([{ trackIndex: props.trackIndex, visual: { isActiveTrack: true }, events: activeTrackTiming.value }], props.playbackTimeMs)
    : new Map()
  for (const midi of heldKeyboardNotes.value) {
    states.set(midi, { count: (states.get(midi)?.count ?? 0) + 1, color: getSynthTrackColor(props.trackIndex).solid })
  }
  return states
})
const canInsert = computed(() => Boolean(track.value))
const statsLabel = computed(() => `${track.value.sequence.length} events · ${enabledLayerCount(track.value)} enabled layers`)
const pitchRange = Object.freeze({ minMidi: 24, maxMidi: 84 })
const durationDivisions = ['1/1', '1/2', '1/4', '1/8', '1/16', '1/32']
const selectedDurationDivision = computed(() => selectedEvent.value ? closestDivisionLabel(selectedEvent.value.durationTicks) : '1/16')
const selectedDurationLabel = computed(() => selectedEvent.value ? ticksToNoteValue(selectedEvent.value.durationTicks) : '')

watch(() => props.trackIndex, () => {
  resetEventSelection()
  selectedPitch.value = SYNTH_DEFAULT_NOTE
  resetInsertTick()
  heldKeyboardNotes.value = new Set()
})

watch(() => [props.playing, props.playbackTimeMs], () => {
  if (!props.playing) return
  timelineRef.value?.focusTime(props.playbackTimeMs)
})

// Context-sensitive duration control: with a selection the grid retunes that event,
// without one it only sets the duration of the next entered note.
watch(() => synth.value.tempo.grid, (division) => {
  if (composerMode.value !== 'compose' || selectedEventIndex.value < 0 || !selectedEvent.value) return
  store.updateSynthEvent(props.synth.uuid, props.trackIndex, selectedEventIndex.value, { durationTicks: noteDivisionToTicks(division) })
})

watch(() => track.value.sequence, () => {
  if (selectedEventIndex.value >= track.value.sequence.length) selectedEventIndex.value = track.value.sequence.length - 1
}, { deep: true })

function renameTrack(name) {
  emit('rename-track', { index: props.trackIndex, name })
}

function changeInstrument(instrument) {
  emit('update-track', { index: props.trackIndex, patch: applyInstrumentPreset(track.value, instrument) })
}

function updateTrackVolume(value) {
  const volume = Math.max(0, Math.min(1, Number(value) || 0))
  emit('update-track', { index: props.trackIndex, patch: { volume } })
}

function updateSound(patch) {
  emit('update-track', { index: props.trackIndex, patch })
}

function resetSound() {
  emit('update-track', { index: props.trackIndex, patch: applyInstrumentPreset(track.value, track.value.instrument) })
}

function selectEvent(selection) {
  if (typeof selection === 'object' && selection?.trackIndex !== props.trackIndex) return
  const index = typeof selection === 'number' ? selection : selection?.eventIndex
  if (!Number.isInteger(index) || !track.value.sequence[index]) return
  setSelectedEvent(index)
  const event = track.value.sequence[index]
  if (event.note != null) selectedPitch.value = event.note
  // Selecting an event parks the composer cursor on it, ready for step entry.
  setInsertTick(event.startTick)
}

function auditionEvent(selection) {
  if (selection?.trackIndex !== props.trackIndex) return
  const event = track.value.sequence[selection?.eventIndex]
  if (event?.note != null) void auditionTrackNote(event.note, event.durationTicks)
}

function selectPitch(midi) {
  // Compose mode enters a new note; pitch of an existing event is never mutated.
  if (composerMode.value === 'compose') {
    insertNote(midi)
    return
  }
  setSelectedPitch(midi)
}

async function auditionPitch(midi) {
  // Compose mode auditions from insertNote(), so the piano press stays a single note.
  if (composerMode.value === 'compose') return
  await auditionTrackNote(midi, defaultGridDurationTicks())
}

async function auditionTrackNote(midi, durationTicks) {
  const durationMs = ticksToMs(durationTicks, synth.value.tempo.bpm)
  await engine.auditionNote(track.value, midi, Math.max(150, Math.min(1000, durationMs)))
}

// Existing events keep their own durationTicks, never the current grid duration.
async function auditionTrackEvents(events) {
  const notes = events.map(event => ({
    note: event.note,
    durationMs: ticksToMs(event.durationTicks, synth.value.tempo.bpm),
  }))
  if (!notes.length) return
  await engine.auditionNotes(track.value, notes)
}

function appendNote() {
  const result = store.appendSynthEvent(props.synth.uuid, props.trackIndex, { note: selectedPitch.value ?? SYNTH_DEFAULT_NOTE, durationTicks: defaultGridDurationTicks() })
  if (result.ok) selectedEventIndex.value = result.index
}

// Single entry point for step entry: PC keyboard and the onscreen piano both land here.
function insertNote(midi) {
  if (!Number.isInteger(midi) || midi < 0 || midi > 127) return false
  const durationTicks = defaultGridDurationTicks()
  const result = store.insertSynthEvent(props.synth.uuid, props.trackIndex, -1, {
    note: midi,
    startTick: insertTick.value,
    durationTicks,
  })
  if (!result.ok) return false
  // Entering a note never replaces the selected event; it drops the selection instead.
  resetEventSelection()
  setSelectedPitch(midi)
  void auditionTrackNote(midi, durationTicks)
  advanceInsertTick(durationTicks)
  ensureInsertCursorVisible()
  return true
}

function insertRest() {
  advanceInsertTick(defaultGridDurationTicks())
  ensureInsertCursorVisible()
}

// Single entry point for delete: Backspace, the ⌫ button and the panel Delete land here.
// With a selection it removes exactly that event, otherwise it falls back to step entry.
function deleteComposerEvent() {
  const eventIndex = selectedEventIndex.value
  const selected = selectedEvent.value
  if (eventIndex < 0 || !selected) return deletePreviousInsertEvent()
  const startTick = selected.startTick
  if (!store.removeSynthEvent(props.synth.uuid, props.trackIndex, eventIndex)) return false
  resetEventSelection()
  setInsertTick(startTick)
  ensureInsertCursorVisible()
  return true
}

function deletePreviousInsertEvent() {
  const eventIndex = findPreviousInsertEventIndex(track.value.sequence, insertTick.value)
  if (eventIndex < 0) {
    setInsertTick(Math.max(0, insertTick.value - defaultGridDurationTicks()))
    ensureInsertCursorVisible()
    return false
  }
  const startTick = track.value.sequence[eventIndex].startTick
  if (!store.removeSynthEvent(props.synth.uuid, props.trackIndex, eventIndex)) return false
  if (selectedEventIndex.value >= track.value.sequence.length) resetEventSelection()
  setInsertTick(startTick)
  ensureInsertCursorVisible()
  return true
}

// Moves the composer cursor between existing note starts on the active track only and
// selects the event there. It never adds or changes an event.
function navigateInsertCursor(direction, audition = false) {
  const startTick = findAdjacentNoteStart(track.value.sequence, insertTick.value, direction)
  if (startTick == null) return false
  setInsertTick(startTick)
  selectEventAtTick(startTick)
  ensureInsertCursorVisible()
  if (audition) void auditionTrackEvents(findNoteEventsAtStart(track.value.sequence, startTick))
  return true
}

// A chord is one navigation position: the first event on that tick becomes the selection.
function selectEventAtTick(startTick) {
  const index = track.value.sequence.findIndex(event => event?.note != null && event.startTick === startTick)
  if (index < 0) {
    resetEventSelection()
    return
  }
  setSelectedEvent(index)
  const note = track.value.sequence[index]?.note
  if (note != null) selectedPitch.value = note
}

function ensureInsertCursorVisible() {
  timelineRef.value?.ensureTickVisible(insertTick.value)
}

// The header grid selector stays the single source of truth for the visible duration.
// The watcher below applies it to the selected event when there is one.
function setInsertDuration(division) {
  emit('update-grid', division)
}

function toggleCompositionAssist() {
  compositionAssist.value = { ...compositionAssist.value, enabled: !compositionAssist.value.enabled }
}

function selectCompositionAssist(value) {
  const parsed = parseCompositionAssistValue(value)
  if (!parsed) return
  compositionAssist.value = { ...compositionAssist.value, ...parsed }
}

// Shared by the [ / ] keys and the − / + buttons next to the piano.
function shiftKeyboardOctave(direction) {
  const next = clampKeyboardBaseMidi(keyboardBaseMidi.value + Math.trunc(direction) * 12, pitchRange.minMidi, pitchRange.maxMidi)
  if (next === keyboardBaseMidi.value) return false
  keyboardBaseMidi.value = next
  heldKeyboardNotes.value = new Set()
  return true
}

// Empty timeline space clears the selection and parks the cursor on the snapped tick.
function placeComposerCursor({ trackIndex, tick }) {
  if (trackIndex !== props.trackIndex) return
  resetEventSelection()
  setInsertTick(tick, defaultGridDurationTicks())
}

function handleComposerKeydown(event) {
  const mode = composerModeForShortcut(event.key)
  if (mode) {
    event.preventDefault()
    setComposerMode(mode)
    return
  }
  if (composerMode.value !== 'compose') return
  if (isTextInputTarget(event.target)) return
  if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
    if (event.ctrlKey || event.metaKey) return
    event.preventDefault()
    // Plain arrows may repeat; Option auditions, so repeat would stack overlapping notes.
    if (event.altKey && event.repeat) return
    navigateInsertCursor(event.key === 'ArrowRight' ? 1 : -1, event.altKey)
    return
  }
  if (event.altKey && !event.ctrlKey && !event.metaKey) {
    const division = insertDurationForShortcut(event.code)
    if (!division) return
    event.preventDefault()
    setInsertDuration(division)
    return
  }
  if (event.metaKey || event.ctrlKey || event.altKey) return
  if (event.key === 'Backspace') {
    event.preventDefault()
    if (!event.repeat) deleteComposerEvent()
    return
  }
  if (event.code === 'Space') {
    event.preventDefault()
    if (!event.repeat) insertRest()
    return
  }
  if (event.key === '[' || event.key === ']') {
    event.preventDefault()
    shiftKeyboardOctave(event.key === '[' ? -1 : 1)
    return
  }
  const midi = keyboardMidi(event.key, keyboardBaseMidi.value)
  if (midi == null) return
  event.preventDefault()
  if (event.repeat) return
  if (!insertNote(midi)) return
  heldKeyboardNotes.value = new Set(heldKeyboardNotes.value).add(midi)
}

function handleComposerKeyup(event) {
  const midi = keyboardMidi(event.key, keyboardBaseMidi.value)
  if (midi == null || !heldKeyboardNotes.value.has(midi)) return
  const next = new Set(heldKeyboardNotes.value)
  next.delete(midi)
  heldKeyboardNotes.value = next
}

function updateSelectedDurationDivision(value) {
  if (selectedEventIndex.value < 0) return
  const durationTicks = noteDivisionToTicks(value)
  store.updateSynthEvent(props.synth.uuid, props.trackIndex, selectedEventIndex.value, { durationTicks })
}

function enabledLayerCount(currentTrack) {
  return currentTrack.layers.filter(layer => layer.enabled).length
}


function noteName(note) {
  return midiToNoteName(note)
}

function defaultGridDurationTicks() {
  return noteDivisionToTicks(synth.value.tempo.grid)
}

onMounted(() => {
  window.addEventListener('keydown', handleComposerKeydown)
  window.addEventListener('keyup', handleComposerKeyup)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleComposerKeydown)
  window.removeEventListener('keyup', handleComposerKeyup)
  engine.dispose()
})

</script>
